<template>
  <section class="mx-4 h-full">
    <BaseTable
      v-if="salesPersons"
      :headers="salespersonsHeaders"
      :list="salesPersons"
      @add-new="onAddNewSalesperson"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="salesPersons"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #controls>
        <RouterLink
          to="salesPersons-hierarchy"
          class="px-2 py-1 text-sm font-normal capitalize rounded-md border border-cyan-light bg-cyan-light text-theme-500"
          >{{ $t('salesperson.viewHierarchy') }}</RouterLink
        >
      </template>
      <template #actions="{ item }: any">
        <div class="flex gap-1 justify-center items-center">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as SalesPerson)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { removeSalesPersonsByIds } from '@/services/api/user';
import { filterActive, filterData, sortData } from '@/utils/table';

import BaseTable from '@/components/common/BaseTable.vue';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TableFilterData } from '@/types/Base';
import { type SalesPerson, eOnlineSearchLevel } from '@/types/SalesPersons';

import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { RouterLink, useRouter } from 'vue-router';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const { salespersons } = storeToRefs(useLookupsStore());

const { getSalesPersonsLookup } = useLookupsStore();

const salespersonsHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'user.userName', text: $t('salesperson.userName'), type: 'text', active: true },
  { value: 'mwSalesPersonType.name', text: $t('salesperson.type'), type: 'text', active: true },
  { value: 'companySite.name', text: $t('salesperson.companySite'), type: 'text', active: true },
  {
    value: 'mwSalesPersonsAccessMaxLevel.name',
    text: $t('salesperson.maxAccessLevel'),
    type: 'text',
    active: true,
  },
  {
    value: 'onlineSearchLevelValue',
    text: $t('salesperson.onlineSearchLevelValue'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const salesPersons = ref<SalesPerson[]>([]);
const filterIsActive = ref<boolean | undefined>();

function getOnlineSearchLevelString(eType: number): string {
  switch (eType) {
    case eOnlineSearchLevel.UpToMaxAccessLevel:
      return 'Up to max access level';
    case eOnlineSearchLevel.All:
      return 'All';
    default:
      return 'Unknown';
  }
}

async function loadData(data?: TableFilterData) {
  salespersons.value = [];
  await getSalesPersonsLookup();
  let response = salespersons.value;
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, salespersonsHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  salesPersons.value = response.map((item) => ({
    ...item,
    onlineSearchLevelValue: getOnlineSearchLevelString(item.onlineSearchLevel),
  }));
}

async function onDeleteMultiselect(items: SalesPerson[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  if (idsToDelete.length === 0) {
    toastStore.warning({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  const res = await removeSalesPersonsByIds(idsToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  await loadData();
}

async function onDeleteRow(item: SalesPerson) {
  const res = await removeSalesPersonsByIds([item.id]);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  await loadData();
}

const router = useRouter();
function onItemClick(item: SalesPerson) {
  router.push({ name: 'salespersons-management-details', params: { id: item.id } });
}

function onAddNewSalesperson() {
  router.push({ name: 'salespersons-management-details', params: { id: uuidv4() } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
